import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function MortgageProtectionFishersFamilyStory() {
  useEffect(() => {
    const SITE_URL = 'https://hoosierlifeinsurance.com';
    document.title = "Mortgage Protection Saved a Fishers Family's Home";

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'The Anderson family in Fishers, Indiana almost lost their home after a tragedy. Learn how mortgage protection insurance kept them in their house and what it costs.');

    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'mortgage protection insurance Fishers Indiana, mortgage protection story, mortgage protection Indiana families');

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/resources/mortgage-protection-fishers-family-story`;

    // Inject Schema.org JSON-LD
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'How Mortgage Protection Saved a Fishers Family\'s Home',
        description: 'Real story: How a Fishers family kept their home after an unexpected loss, thanks to mortgage protection insurance. Learn why every Indiana homeowner needs this coverage.',
        author: {
          '@type': 'Person',
          name: 'Benjamin Wright'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Hoosier Life Insurance',
          url: SITE_URL
        },
        datePublished: '2025-01-10T00:00:00Z',
        dateModified: new Date().toISOString(),
        url: `${SITE_URL}/resources/mortgage-protection-fishers-family-story`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/resources/mortgage-protection-fishers-family-story`
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Resources',
            item: `${SITE_URL}/resources`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'How Mortgage Protection Saved a Fishers Family\'s Home',
            item: `${SITE_URL}/resources/mortgage-protection-fishers-family-story`
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'How Mortgage Protection Saved a Fishers Family\'s Home',
        description: 'Real story: How a Fishers family kept their home after an unexpected loss, thanks to mortgage protection insurance. Learn why every Indiana homeowner needs this coverage.',
        url: `${SITE_URL}/resources/mortgage-protection-fishers-family-story`,
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          url: SITE_URL,
          name: 'Hoosier Life Insurance'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How does mortgage protection insurance work for Indiana homeowners?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Mortgage protection insurance is a term life policy sized to cover your mortgage balance. If you die during the policy term, your beneficiary receives the full death benefit tax-free and can use it to pay off the mortgage, keeping the family in the home. Unlike lender-sold mortgage insurance where the bank is the beneficiary, a personal mortgage protection policy pays your family directly—giving them the choice of paying off the mortgage, continuing to make payments, or using the money for other needs.'
            }
          },
          {
            '@type': 'Question',
            name: 'How quickly does mortgage protection life insurance pay out after a claim?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Most carriers pay life insurance claims within 2-4 weeks of receiving a completed claim form and certified death certificate. In straightforward cases like the Anderson family story from Fishers, Indiana, claims are often paid in as little as 12-14 days. This speed is critical because families need funds quickly to cover funeral costs, medical bills, and the next mortgage payment without falling behind.'
            }
          },
          {
            '@type': 'Question',
            name: 'How much does mortgage protection insurance cost for Indiana homeowners?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Most Indiana families can get mortgage protection coverage for $50-$100 per month depending on age, health, and coverage amount. For example, the Anderson family in Fishers paid $85/month for $400,000 of 30-year term coverage for both spouses. A single 35-year-old in Indianapolis can typically get $300,000 in 30-year term coverage for $30-$45/month. Getting your own policy is 2-3 times cheaper than lender-sold mortgage insurance.'
            }
          },
          {
            '@type': 'Question',
            name: 'What is the difference between lender mortgage insurance and personal mortgage protection?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Lender mortgage insurance (sold by banks at closing) pays the bank directly and has a decreasing death benefit as you pay down your loan—while your premium stays the same. Personal mortgage protection term life insurance pays your family directly, has a level death benefit, and typically costs 2-3 times less. With a personal policy, your family decides whether to pay off the mortgage, keep making payments, or use the money for other needs. Always buy your own policy rather than the lender\'s mortgage insurance.'
            }
          },
          {
            '@type': 'Question',
            name: 'Do I need mortgage protection insurance if I already have term life insurance?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'If you already have sufficient term life coverage (typically 10-12 times your annual income plus your mortgage balance), you may not need a separate mortgage protection policy. However, many Indiana homeowners find their existing coverage is insufficient. Check that your current death benefit would cover your mortgage payoff, income replacement for 10+ years, college costs for your children, and final expenses. If your existing policy falls short, adding mortgage protection coverage is a smart, affordable fix.'
            }
          }
        ]
      }
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `schema-resource-mortgage-fishers-${index}`;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-resource-mortgage-fishers-${index}`);
        if (script) {
          document.head.removeChild(script);
        }
      });
    };
  }, []);

  return (
    <>
      <title>Mortgage Protection Saved a Fishers Family&apos;s Home | Hoosier Life Insurance</title>
      <article className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white py-20">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-4xl mx-auto px-6">
            <div className="mb-6">
              <Link to="/resources" className="inline-flex items-center text-white/70 hover:text-white transition-colors text-sm">
                <i className="ri-arrow-left-line mr-2"></i>
                Back to Resources
              </Link>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">How Mortgage Protection Insurance Saved the Anderson Family's Home</h1>
            <p className="text-xl text-white/80 mb-8">A real Fishers, Indiana family's story about the importance of protecting your home and family's future</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span><i className="ri-calendar-line mr-2"></i>January 2026</span>
              <span><i className="ri-time-line mr-2"></i>7 min read</span>
              <span><i className="ri-map-pin-line mr-2"></i>Fishers, Indiana</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              When Tom and Jennifer Anderson bought their dream home in Fishers in 2019, they never imagined that five years later, mortgage protection insurance would be the only thing standing between their family and foreclosure.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This is their story—and why every Indiana homeowner should consider protecting their family's most valuable asset.
            </p>
          </div>

          {/* Family Profile */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 bg-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-home-heart-line text-3xl"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Meet the Anderson Family</h2>
                <p className="text-gray-700">Fishers, Indiana • Hamilton County</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Family Details</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-user-line text-teal-600 mr-3 mt-1"></i>
                    <span><strong>Tom Anderson:</strong> Age 38, IT manager at Eli Lilly</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-user-line text-teal-600 mr-3 mt-1"></i>
                    <span><strong>Jennifer Anderson:</strong> Age 36, stay-at-home mom</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-team-line text-teal-600 mr-3 mt-1"></i>
                    <span><strong>Children:</strong> Emma (8), Lucas (5), Olivia (3)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-money-dollar-circle-line text-teal-600 mr-3 mt-1"></i>
                    <span><strong>Household Income:</strong> $95,000/year</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Home Details</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <i className="ri-home-4-line text-teal-600 mr-3 mt-1"></i>
                    <span><strong>Location:</strong> Sunflower neighborhood, Fishers</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-price-tag-3-line text-teal-600 mr-3 mt-1"></i>
                    <span><strong>Purchase Price:</strong> $385,000 (2019)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-bank-line text-teal-600 mr-3 mt-1"></i>
                    <span><strong>Mortgage Balance:</strong> $320,000 (30-year loan)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-calendar-check-line text-teal-600 mr-3 mt-1"></i>
                    <span><strong>Monthly Payment:</strong> $2,400 (PITI)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* The Decision */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Decision That Changed Everything</h2>
            <div className="bg-white border-l-4 border-teal-600 p-6 rounded-r-lg mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong className="text-gray-900">"When we bought our home in 2019, our mortgage broker mentioned mortgage protection insurance,"</strong> Jennifer recalls. <em>"Tom was hesitant—we were already stretching our budget with the down payment, closing costs, and new furniture. But I insisted we at least talk to an agent."</em>
              </p>
              <p className="text-gray-700 leading-relaxed">
                <em>"The agent explained that if something happened to Tom, I'd be left with a $2,400/month mortgage payment and no income. With three kids under 10, I couldn't just go back to work full-time. That conversation scared me enough to push for it."</em>
              </p>
            </div>

            <div className="bg-[#FDF6F0] rounded-xl p-6 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What They Purchased</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center pb-3 border-b border-teal-200">
                  <span className="text-gray-700">Policy Type</span>
                  <span className="font-bold text-gray-900">30-Year Term Life Insurance</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-teal-200">
                  <span className="text-gray-700">Coverage Amount</span>
                  <span className="font-bold text-gray-900">$400,000 (Tom) + $150,000 (Jennifer)</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-teal-200">
                  <span className="text-gray-700">Monthly Premium</span>
                  <span className="font-bold text-teal-600">$85/month (both policies)</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700">Purpose</span>
                  <span className="font-bold text-gray-900">Mortgage protection + income replacement</span>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed">
                <strong className="text-gray-900">Tom's initial reaction:</strong> <em>"$85 a month felt like a lot when we were already tight on cash. But Jennifer was right—if I died, she'd be stuck with a mortgage she couldn't afford. We decided to cut back on eating out and streaming services to make room for it."</em>
              </p>
            </div>
          </div>

          {/* The Tragedy */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">March 2024: The Day Everything Changed</h2>
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-heart-pulse-line text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">The Unexpected Heart Attack</h3>
                  <p className="text-gray-700 leading-relaxed">
                    On March 14, 2024, Tom collapsed at work. He was rushed to St. Vincent Hospital in Indianapolis, where doctors discovered he'd suffered a massive heart attack. Despite emergency surgery, Tom passed away three days later at age 38.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6">
                <p className="text-gray-700 leading-relaxed italic">
                  "Tom was healthy. He didn't smoke, he exercised regularly, and he had no family history of heart disease. The doctors said it was a rare genetic condition no one knew about. One day he was coaching Lucas's soccer team, and three days later he was gone. I was in complete shock."
                </p>
                <p className="text-sm text-gray-600 mt-4 text-right">— Jennifer Anderson</p>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">The Immediate Financial Reality</h3>
              <p className="text-gray-700 mb-4">
                Within days of Tom's death, Jennifer faced a crushing financial reality:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Lost income:</strong> $95,000/year household income gone</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Mortgage payment:</strong> $2,400/month still due</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Funeral costs:</strong> $11,500 (not covered by employer life insurance)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Medical bills:</strong> $8,200 after insurance</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-red-500 mr-3 mt-1 text-xl"></i>
                  <span><strong>Savings:</strong> Only $12,000 in emergency fund</span>
                </li>
              </ul>
            </div>
          </div>

          {/* How Insurance Saved Them */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">How Mortgage Protection Insurance Saved Their Home</h2>
            
            <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold mb-6">The Life Insurance Claim Process</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white text-teal-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
                    1
                  </div>
                  <div>
                    <p className="font-bold text-xl mb-2">Day 5: Filed the Claim</p>
                    <p className="text-teal-50">Jennifer called the insurance company and submitted Tom's death certificate. The agent was compassionate and walked her through every step.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white text-teal-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
                    2
                  </div>
                  <div>
                    <p className="font-bold text-xl mb-2">Day 12: Claim Approved</p>
                    <p className="text-teal-50">The insurance company reviewed the claim and approved the full $400,000 death benefit. No issues, no delays.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white text-teal-600 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xl">
                    3
                  </div>
                  <div>
                    <p className="font-bold text-xl mb-2">Day 14: Money Received</p>
                    <p className="text-teal-50">$400,000 deposited directly into Jennifer's bank account—tax-free. Two weeks from Tom's death to financial security.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-teal-500 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How Jennifer Used the $400,000</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Paid off entire mortgage</span>
                  <span className="font-bold text-gray-900">$320,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Funeral and medical bills</span>
                  <span className="font-bold text-gray-900">$19,700</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Emergency fund (6 months expenses)</span>
                  <span className="font-bold text-gray-900">$30,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">529 college savings (3 kids)</span>
                  <span className="font-bold text-gray-900">$20,000</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                  <span className="text-gray-700">Remaining invested for income</span>
                  <span className="font-bold text-gray-900">$10,300</span>
                </div>
                <div className="flex justify-between items-center pt-4 text-xl">
                  <span className="font-bold text-gray-900">TOTAL</span>
                  <span className="font-bold text-teal-600">$400,000</span>
                </div>
              </div>
            </div>
          </div>

          {/* Life After */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Life After the Tragedy: One Year Later</h2>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Where the Anderson Family Is Today</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-home-smile-line text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">Still in Their Home</h4>
                    <p className="text-gray-700 text-sm">
                      The kids are in the same schools, same neighborhood, same friends. No mortgage payment means Jennifer can afford to stay home with them.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-briefcase-line text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">Part-Time Work</h4>
                    <p className="text-gray-700 text-sm">
                      Jennifer started a part-time remote job ($2,000/month) that fits around the kids' schedules. Combined with Social Security survivor benefits, they're financially stable.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-graduation-cap-line text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">College Secured</h4>
                    <p className="text-gray-700 text-sm">
                      The $20,000 in 529 accounts, plus Social Security benefits until age 18, means all three kids can attend Indiana public universities.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6">
                    <div className="w-12 h-12 bg-teal-100 text-teal-600 rounded-lg flex items-center justify-center mb-4">
                      <i className="ri-heart-line text-2xl"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-3">Emotional Stability</h4>
                    <p className="text-gray-700 text-sm">
                      Not having to worry about losing their home or moving the kids allowed the family to grieve and heal without financial panic.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-teal-600 text-white rounded-xl p-6">
                <p className="text-lg leading-relaxed italic mb-4">
                  "That $85 a month we spent on life insurance saved our lives. If we hadn't had that policy, I would have lost the house within 6 months. We'd be living in an apartment, the kids would have changed schools, and I'd be working two jobs just to survive. Instead, we're still in our home, the kids are thriving, and I can be the mom they need during this impossible time."
                </p>
                <p className="text-teal-100 font-bold">— Jennifer Anderson, Fishers, IN</p>
              </div>
            </div>
          </div>

          {/* What If They Hadn't Had Insurance */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What If They Hadn't Had Mortgage Protection Insurance?</h2>
            <div className="bg-red-50 border-2 border-red-500 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">The Alternate Reality (Without Insurance)</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border-l-4 border-red-500">
                  <p className="font-bold text-gray-900 mb-2">Month 1-3: Survival Mode</p>
                  <p className="text-gray-700 text-sm">
                    Jennifer uses the $12,000 emergency fund to cover mortgage payments. Funeral and medical bills go on credit cards ($19,700 debt at 22% APR).
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 border-l-4 border-red-500">
                  <p className="font-bold text-gray-900 mb-2">Month 4-6: Desperation</p>
                  <p className="text-gray-700 text-sm">
                    Emergency fund depleted. Jennifer takes two part-time jobs ($3,500/month) but still can't cover the $2,400 mortgage + childcare ($1,800/month). Misses first mortgage payment.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 border-l-4 border-red-500">
                  <p className="font-bold text-gray-900 mb-2">Month 7-9: Foreclosure Begins</p>
                  <p className="text-gray-700 text-sm">
                    Three missed mortgage payments. Bank sends foreclosure notice. Jennifer tries to sell the house but owes more than it's worth after realtor fees.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 border-l-4 border-red-500">
                  <p className="font-bold text-gray-900 mb-2">Month 10-12: Forced to Move</p>
                  <p className="text-gray-700 text-sm">
                    House goes into foreclosure. Jennifer and the kids move into a 2-bedroom apartment in a different school district. Kids lose their friends, their schools, and their stability during the worst year of their lives.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 border-l-4 border-red-500">
                  <p className="font-bold text-gray-900 mb-2">Long-Term Impact</p>
                  <p className="text-gray-700 text-sm">
                    Foreclosure ruins Jennifer's credit for 7 years. No college savings. Kids grow up in financial instability. The trauma of losing their dad is compounded by losing their home, their neighborhood, and their childhood.
                  </p>
                </div>
              </div>

              <div className="mt-6 bg-red-600 text-white rounded-lg p-6">
                <p className="text-xl font-bold mb-3">The Cost of Not Having Insurance</p>
                <ul className="space-y-2 text-red-50">
                  <li className="flex items-start">
                    <i className="ri-close-line mr-3 mt-1 text-2xl"></i>
                    <span>Lost home worth $385,000</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line mr-3 mt-1 text-2xl"></i>
                    <span>Foreclosure + credit damage</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line mr-3 mt-1 text-2xl"></i>
                    <span>Kids' emotional trauma from instability</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line mr-3 mt-1 text-2xl"></i>
                    <span>No college savings</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-close-line mr-3 mt-1 text-2xl"></i>
                    <span>Years of financial struggle</span>
                  </li>
                </ul>
                <p className="mt-4 text-lg font-bold">All preventable with $85/month in life insurance.</p>
              </div>
            </div>
          </div>

          {/* Lessons Learned */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">5 Lessons from the Anderson Family's Story</h2>
            <div className="space-y-4">
              <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-number-1 text-teal-600 mr-3 text-2xl"></i>
                  Tragedy Doesn't Discriminate by Age or Health
                </h3>
                <p className="text-gray-700 text-sm pl-11">
                  Tom was 38, healthy, and had no warning signs. If you have a mortgage and dependents, you need life insurance—regardless of how healthy you feel.
                </p>
              </div>

              <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-number-2 text-teal-600 mr-3 text-2xl"></i>
                  Employer Life Insurance Isn't Enough
                </h3>
                <p className="text-gray-700 text-sm pl-11">
                  Tom's employer provided $50,000 in life insurance—barely enough to cover funeral costs and 2 months of expenses. It wouldn't have saved the house.
                </p>
              </div>

              <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-number-3 text-teal-600 mr-3 text-2xl"></i>
                  Stay-at-Home Parents Need Coverage Too
                </h3>
                <p className="text-gray-700 text-sm pl-11">
                  Jennifer's $150,000 policy ensures that if something happens to her, Tom (if he were still alive) could afford childcare and keep working. Both spouses need protection.
                </p>
              </div>

              <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-number-4 text-teal-600 mr-3 text-2xl"></i>
                  The Cost Is Tiny Compared to the Protection
                </h3>
                <p className="text-gray-700 text-sm pl-11">
                  $85/month = $1,020/year. Over 5 years, they paid $5,100 in premiums and received $400,000. That's a 7,800% return on investment.
                </p>
              </div>

              <div className="bg-white border-2 border-teal-500 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <i className="ri-number-5 text-teal-600 mr-3 text-2xl"></i>
                  Life Insurance Gives You Time to Grieve
                </h3>
                <p className="text-gray-700 text-sm pl-11">
                  Jennifer didn't have to rush back to work, sell the house, or uproot the kids. She had the financial breathing room to focus on healing her family.
                </p>
              </div>
            </div>
          </div>

          {/* Jennifer's Message */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Jennifer's Message to Other Indiana Families</h2>
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 bg-white text-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-chat-quote-line text-3xl"></i>
                </div>
                <div>
                  <p className="text-xl leading-relaxed mb-6">
                    "If you're reading this and thinking, 'We can't afford life insurance right now,' I'm begging you to reconsider. We almost didn't buy it. Tom thought it was too expensive. But that $85 a month saved our entire lives."
                  </p>
                  <p className="text-xl leading-relaxed mb-6">
                    "I know it's uncomfortable to think about death. I know it feels like you're wasting money on something you'll never use. But I promise you—if the worst happens, that policy will be the difference between your family surviving or falling apart."
                  </p>
                  <p className="text-xl leading-relaxed mb-6">
                    "Don't wait. Don't assume you're too young or too healthy. Tom was both, and he's gone. Protect your family today, because tomorrow isn't guaranteed."
                  </p>
                  <p className="text-teal-100 font-bold text-lg">— Jennifer Anderson, Fishers, Indiana</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl p-8 mb-12">
            <h2 className="text-3xl font-bold mb-6">Protect Your Indiana Family's Home Today</h2>
            <p className="text-xl text-gray-300 mb-8">
              Don't wait until it's too late. Get a free quote and see how affordable mortgage protection insurance really is.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Link 
                to="/mortgage-protection" 
                className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0F2A3F] transition-colors whitespace-nowrap"
              >
                Get Free Quote
              </Link>
              <Link 
                to="/book-appointment" 
                className="bg-white text-gray-900 px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap"
              >
                Talk to an Agent
              </Link>
            </div>
            <p className="text-sm text-gray-400 mt-6 text-center">
              Most Indiana families qualify for $500,000 in coverage for $50-$75/month
            </p>
          </div>

          {/* Related Articles */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>

            {/* Product Pages — two-way authority signal */}
            <div className="mb-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Product Pages</p>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <Link to="/mortgage-protection" className="group bg-white border-2 border-[#D97642] rounded-xl p-5 hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-home-heart-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Mortgage Protection Insurance</h3>
                  <p className="text-xs text-gray-500">Get your free quote and protect your home today</p>
                </Link>
                <Link to="/term-life" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-calendar-check-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">Term Life Insurance</h3>
                  <p className="text-xs text-gray-500">Cover your mortgage term with affordable protection</p>
                </Link>
                <Link to="/no-medical-exam" className="group bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#D97642] hover:bg-[#FDF6F0] transition-colors">
                  <div className="w-10 h-10 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-3 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                    <i className="ri-stethoscope-line text-xl"></i>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#D97642] transition-colors">No Medical Exam Life Insurance</h3>
                  <p className="text-xs text-gray-500">Fast approval options — no doctor visit required</p>
                </Link>
              </div>
            </div>

            {/* Articles */}
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Articles &amp; Guides</p>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/resources/how-much-life-insurance-indiana-families-need" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-calculator-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">How Much Coverage Do You Need?</h3>
                <p className="text-sm text-gray-600">Calculate your exact coverage need for your family</p>
              </Link>

              <Link to="/resources/term-vs-whole-life-indianapolis-homeowners" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-scales-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Term vs. Whole Life for Homeowners</h3>
                <p className="text-sm text-gray-600">Which policy type best protects your mortgage?</p>
              </Link>

              <Link to="/blog/mortgage-protection-indianapolis" className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className="ri-article-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">Mortgage Protection Indianapolis</h3>
                <p className="text-sm text-gray-600">How to protect your Indianapolis home from $30/month</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Don't Let Your Family's Story End Differently</h2>
            <p className="text-xl text-gray-700 mb-8">
              Talk to an Indiana-based agent today about protecting your home and family's future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3176482983" className="bg-[#1A3A52] text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-[#0F2A3F] transition-colors whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-phone-line mr-2"></i>
                Call (317) 648-2983
              </a>
              <Link to="/contact" className="bg-white text-[#1A3A52] px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100 transition-colors border-2 border-[#1A3A52] whitespace-nowrap inline-flex items-center justify-center">
                <i className="ri-message-3-line mr-2"></i>
                Send a Message
              </Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}